import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { FiMic, FiMicOff } from 'react-icons/fi';

const SpeakAndCreate = forwardRef(({ enabled, onTranscript, isGenerating, onListeningChange }, ref) => {
  const [isListening, setIsListening] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const recognitionRef = useRef(null);
  const startTimeRef = useRef(0);
  const restartAttempts = useRef(0);
  const manualStop = useRef(false); // flag to prevent auto-restart after manual stop
  const MAX_RESTARTS = 5;

  const createRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;

    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (event) => {
      const last = event.results.length - 1;
      if (event.results[last].isFinal) {
        const transcript = event.results[last][0].transcript;
        if (transcript) {
          onTranscript?.(transcript);
        }
      }
    };

    rec.onerror = (event) => {
      console.error('SAC error:', event.error);
      if (event.error === 'not-allowed') {
        setUnsupported(true);
      }
      setIsListening(false);
      restartAttempts.current = 0;
    };

    rec.onend = () => {
      const elapsed = Date.now() - startTimeRef.current;
      console.log('SAC onend. Elapsed:', elapsed, 'Attempts:', restartAttempts.current, 'ManualStop:', manualStop.current);

      // Don't auto-restart if the user stopped manually
      if (manualStop.current) {
        manualStop.current = false;
        setIsListening(false);
        restartAttempts.current = 0;
        return;
      }

      if (elapsed < 3000 && restartAttempts.current < MAX_RESTARTS) {
        restartAttempts.current++;
        setTimeout(() => {
          try {
            rec.start();
            startTimeRef.current = Date.now();
          } catch (e) {
            console.error('Restart failed:', e);
            setIsListening(false);
            restartAttempts.current = 0;
          }
        }, 200);
      } else {
        setIsListening(false);
        restartAttempts.current = 0;
      }
    };

    return rec;
  };

  // Notify parent when listening state changes
  useEffect(() => {
    onListeningChange?.(isListening);
  }, [isListening, onListeningChange]);

  useEffect(() => {
    recognitionRef.current = createRecognition();
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  const startListening = () => {
    if (!recognitionRef.current || !enabled || isGenerating) return;
    if (isListening) return;

    manualStop.current = false; // allow auto-restart
    restartAttempts.current = 0;
    try {
      recognitionRef.current.start();
      startTimeRef.current = Date.now();
      setIsListening(true);
    } catch (err) {
      console.error('start failed:', err);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      manualStop.current = true;
      recognitionRef.current.stop();
      setIsListening(false);      // immediately update UI
      restartAttempts.current = 0;
    }
  };

  useImperativeHandle(ref, () => ({
    startListening,
    stopListening,
    isListening,
  }));

  if (!enabled || unsupported) return null;

    return (
    <button
      type="button"
      className={`voice-mic-btn ${isListening ? 'listening' : ''}`}
      onClick={() => (isListening ? stopListening() : startListening())}
      disabled={isGenerating}
      title={isListening ? 'Stop listening' : 'Start voice input'}
      aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
    >
      <FiMic size={18} />
    </button>
  );
});

export default SpeakAndCreate;